import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import {
  getAdminUser,
  getUserBookings,
  getUserSubscriptions,
  setCoachRole,
  ApiError,
} from '../../lib/api';
import { Button } from '../../components/Button';
import { EditCreditDialog } from './EditCreditDialog';
import { IssueSubscriptionDialog } from './IssueSubscriptionDialog';
import { EditUserModal } from './EditUserModal';
import { SubscriptionsList } from './SubscriptionsList';
import { BookingsHistoryList } from './BookingsHistoryList';
import { TtarLinkPanel } from './TtarLinkPanel';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { fullName } from '../../lib/format';
import { formatLocal, formatVenueTime } from '../../lib/time';
import type {
  AdminLookupUser,
  Booking,
  Paged,
  SubscriptionWithUsage,
  ActiveSeriesExistDetail,
} from '../../lib/types';

const BOOKING_PAGE_SIZE = 25;

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();

  const userId = id ?? '';
  const validId = userId.length > 0;

  const [bookingsOffset, setBookingsOffset] = useState(0);
  const [editCreditOpen, setEditCreditOpen] = useState(false);
  const [issueSubOpen, setIssueSubOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [coachBusy, setCoachBusy] = useState(false);

  const userLoader = useCallback(
    (signal: AbortSignal) => getAdminUser(userId, signal),
    [userId],
  );
  const bookingsLoader = useCallback(
    (signal: AbortSignal) =>
      getUserBookings(userId, { limit: BOOKING_PAGE_SIZE, offset: bookingsOffset }, signal),
    [userId, bookingsOffset],
  );
  const subsLoader = useCallback(
    (signal: AbortSignal) => getUserSubscriptions(userId, signal),
    [userId],
  );

  const user = useApi<AdminLookupUser>(userLoader);
  const bookings = useApi<Paged<Booking>>(bookingsLoader);
  const subs = useApi<SubscriptionWithUsage[]>(subsLoader);

  if (!validId) return <div style={{ ...T.ui, color: PS.danger }}>Invalid user id.</div>;

  const handleToggleCoach = async () => {
    if (!user.data) return;
    setCoachBusy(true);
    try {
      await setCoachRole(user.data.id, !user.data.is_coach);
      push('success', user.data.is_coach ? 'Demoted.' : 'Promoted to coach.');
      user.refetch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const d = err.detail as ActiveSeriesExistDetail | undefined;
        const n = d?.active_series_count ?? 0;
        push('error', `Cannot demote — ${n} active series. End them on /coaches first.`);
      } else {
        push('error', err instanceof ApiError ? err.message : 'Toggle failed');
      }
    } finally {
      setCoachBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Button variant="secondary" onClick={() => navigate('/users')}>← Back</Button>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>
          {user.data ? fullName(user.data) : '…'}
        </h1>
        <Button onClick={() => setEditCreditOpen(true)} disabled={!user.data}>Edit credit</Button>
        <Button onClick={() => setIssueSubOpen(true)} disabled={!user.data}>Issue subscription</Button>
        <Button variant="secondary" onClick={() => setEditOpen(true)} disabled={!user.data}>Edit</Button>
        {user.data && (
          <Button
            variant={user.data.is_coach ? 'danger' : 'secondary'}
            onClick={() => void handleToggleCoach()}
            disabled={coachBusy}
          >
            {user.data.is_coach ? 'Demote coach' : 'Promote to coach'}
          </Button>
        )}
      </header>

      {user.loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}
      {user.error && (
        <div style={errBanner}>{user.error.message}</div>
      )}

      {user.data && (
        <>
          <section style={card}>
            <Row label="ID" value={user.data.id} />
            <Row label="Phone" value={user.data.phone ?? '—'} />
            <Row label="Email" value={user.data.email ?? '—'} />
            <Row label="Telegram" value={user.data.telegram_id?.toString() ?? '—'} />
            <Row label="Language" value={user.data.language} />
            <Row label="Credit" value={`${user.data.credit_minutes} min`} />
            <Row label="Flags" value={[user.data.is_admin && 'admin', user.data.is_coach && 'coach'].filter(Boolean).join(', ') || '—'} />
            <Row label="Created" value={`${formatLocal(user.data.created_at, 'yyyy-MM-dd')} ${formatVenueTime(user.data.created_at)}`} />
          </section>
          <TtarLinkPanel userId={user.data.id} />
        </>
      )}

      <section>
        <h2 style={{ ...T.subheading, margin: '0 0 8px' }}>Subscriptions</h2>
        {subs.loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}
        {subs.error && <div style={errBanner}>{subs.error.message}</div>}
        {subs.data && (
          <SubscriptionsList subscriptions={subs.data} onChanged={() => subs.refetch()} />
        )}
      </section>

      <section>
        <h2 style={{ ...T.subheading, margin: '0 0 8px' }}>Bookings</h2>
        {bookings.error && <div style={errBanner}>{bookings.error.message}</div>}
        {bookings.data && (
          <BookingsHistoryList
            page={bookings.data}
            onPageChange={(o) => setBookingsOffset(o)}
            loading={bookings.loading}
          />
        )}
      </section>

      {user.data && (
        <>
          <EditCreditDialog
            open={editCreditOpen}
            userId={user.data.id}
            firstName={fullName(user.data)}
            currentMinutes={user.data.credit_minutes}
            onClose={() => setEditCreditOpen(false)}
            onSaved={() => user.refetch()}
          />
          <IssueSubscriptionDialog
            open={issueSubOpen}
            userId={user.data.id}
            firstName={fullName(user.data)}
            onClose={() => setIssueSubOpen(false)}
            onIssued={() => subs.refetch()}
          />
          <EditUserModal
            open={editOpen}
            user={user.data}
            onClose={() => setEditOpen(false)}
            onSaved={() => user.refetch()}
          />
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ ...T.label, width: 120, flexShrink: 0 }}>{label}</div>
      <div style={{ ...T.ui, color: PS.nearBlack, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

const card = {
  background: PS.white,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 4,
  padding: 16,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 8,
};

const errBanner = {
  padding: 12, background: PS.errorBg, border: `1px solid ${PS.errorBorder}`,
  color: PS.errorText, borderRadius: 3,
};
